import React, { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Pencil, Trash2, FileText } from 'lucide-react';
import { toast } from 'sonner';

const TIPOS = ['Cópia simples', 'Inteiro Teor'];

const emptyForm = {
  number: '', notary_office: '', issue_date: '',
  registration_type: 'Cópia simples', notes: '',
};

export default function RegistrationsTab({ projectId, canEdit }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/registrations`);
      setItems(data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao carregar matrículas');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditingId(item.registration_id);
    setForm({
      number: item.number || '',
      notary_office: item.notary_office || '',
      issue_date: item.issue_date || '',
      registration_type: item.registration_type || 'Cópia simples',
      notes: item.notes || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.number.trim()) {
      toast.error('Informe o número da matrícula');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        number: form.number,
        notary_office: form.notary_office || null,
        issue_date: form.issue_date || null,
        registration_type: form.registration_type,
        notes: form.notes || null,
      };
      if (editingId) {
        await api.put(`/registrations/${editingId}`, payload);
        toast.success('Matrícula atualizada');
      } else {
        await api.post(`/projects/${projectId}/registrations`, payload);
        toast.success('Matrícula cadastrada');
      }
      setDialogOpen(false);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Confirmar exclusão da matrícula?')) return;
    try {
      await api.delete(`/registrations/${id}`);
      toast.success('Matrícula excluída');
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao excluir');
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
  };

  return (
    <Card className="p-6 border border-gray-200">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Matrículas do Imóvel</h2>
          <p className="text-sm text-gray-500 mt-0.5">Cadastros emitidos por cartório vinculados ao projeto</p>
        </div>
        {canEdit && (
          <Button onClick={openCreate} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="add-registration-button">
            <Plus className="w-4 h-4 mr-2" />
            Nova Matrícula
          </Button>
        )}
      </div>

      {loading ? (
        <p className="text-sm text-gray-500 py-6 text-center">Carregando...</p>
      ) : items.length === 0 ? (
        <div className="text-center py-12">
          <FileText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">Nenhuma matrícula cadastrada</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Número</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Cartório</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Data Emissão</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Tipo</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Observações</th>
                {canEdit && <th className="text-right py-3 px-4 font-medium text-gray-700">Ações</th>}
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.registration_id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`registration-row-${item.registration_id}`}>
                  <td className="py-3 px-4 font-medium text-gray-900">{item.number}</td>
                  <td className="py-3 px-4 text-gray-700">{item.notary_office || '—'}</td>
                  <td className="py-3 px-4 text-gray-700">{formatDate(item.issue_date)}</td>
                  <td className="py-3 px-4">
                    <Badge variant="outline" className={item.registration_type === 'Inteiro Teor' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-blue-50 text-blue-700 border-blue-200'}>
                      {item.registration_type}
                    </Badge>
                  </td>
                  <td className="py-3 px-4 text-gray-600 max-w-xs truncate" title={item.notes || ''}>{item.notes || '—'}</td>
                  {canEdit && (
                    <td className="py-3 px-4 text-right">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(item)} data-testid={`edit-registration-${item.registration_id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(item.registration_id)} data-testid={`delete-registration-${item.registration_id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Editar matrícula' : 'Nova matrícula'}</DialogTitle>
            <DialogDescription>Cadastre os dados da matrícula do imóvel vinculada ao projeto.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
            <div className="md:col-span-2">
              <Label>Número *</Label>
              <Input value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} data-testid="registration-form-number" />
            </div>
            <div>
              <Label>Nome do cartório</Label>
              <Input value={form.notary_office} onChange={(e) => setForm({ ...form, notary_office: e.target.value })} data-testid="registration-form-notary" />
            </div>
            <div>
              <Label>Data de emissão</Label>
              <Input type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} data-testid="registration-form-date" />
            </div>
            <div className="md:col-span-2">
              <Label>Tipo da matrícula *</Label>
              <Select value={form.registration_type} onValueChange={(v) => setForm({ ...form, registration_type: v })}>
                <SelectTrigger data-testid="registration-form-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIPOS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label>Observações</Label>
              <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="registration-form-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="registration-form-save">
              {saving ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
